import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BreadcrumbNav, Skeleton, StatusMessage } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Filters } from '../../components/uiChrome.jsx';
import { useWorkspacesDistribution } from '../../hooks';
import WorkspaceManagePanel from './WorkspaceManagePanel.jsx';
import { AlertTriangle, ArrowRight, Blocks } from 'lucide-react';

const PAGE_WRAP = 'flex min-h-0 flex-1 flex-col bg-surface-base';
const PAGE_INNER = 'px-6';
const PAGE_SIZE = 12;

const sortValue = (row, key) => {
  if (key === 'members') return row.member_count ?? 0;
  if (key === 'integrations') return row.integrations?.length ?? 0;
  return row.workspace ?? '';
};

/**
 * Workspaces admin list (CT-4209). Full-width (`px-6`) workspace-first view of
 * distribution — search + sort, then a card grid matching the prototype
 * (`ws-grid` / `ws-card`). A card click opens the per-workspace manage panel.
 */
const WorkspacesPage = () => {
  const { t } = useTranslation('admin-studio');
  const navigate = useNavigate();
  const { rows, isLoading, error, refetch } = useWorkspacesDistribution();
  const [managing, setManaging] = useState(null);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let next = rows;
    if (q) {
      next = rows.filter((row) => {
        const hay = `${row.workspace} ${row.desc} ${row.owner}`.toLowerCase();
        return hay.includes(q);
      });
    }
    const dir = sortDir === 'desc' ? -1 : 1;
    return [...next].sort((a, b) => {
      const av = sortValue(a, sortKey);
      const bv = sortValue(b, sortKey);
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' }) * dir;
    });
  }, [rows, search, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const visible = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  const searchProps = {
    value: search,
    onChange: (e) => {
      setSearch(e?.target ? e.target.value : e);
      setPage(1);
    },
    onClear: () => {
      setSearch('');
      setPage(1);
    },
    placeholder: t('integrations.workspaces.searchPlaceholder'),
  };

  const sort = {
    value: sortKey,
    defaultValue: 'name',
    direction: sortDir,
    defaultDirection: 'asc',
    onChange: (next) => {
      setSortKey(next);
      setSortDir(next === 'name' ? 'asc' : 'desc');
      setPage(1);
    },
    onDirectionChange: (next) => {
      setSortDir(next);
      setPage(1);
    },
    options: [
      { value: 'name', label: t('integrations.workspaces.sortName') },
      { value: 'members', label: t('integrations.workspaces.sortMembers') },
      { value: 'integrations', label: t('integrations.workspaces.sortIntegrations') },
    ],
  };

  const handleClearAll = useCallback(() => {
    setSearch('');
    setPage(1);
  }, []);

  return (
    <main className={PAGE_WRAP}>
      <div className={`${PAGE_INNER} flex-1 overflow-y-auto pb-4 pt-3.5 scrollbar-aimsos-auto-sm`}>
        <BreadcrumbNav
          size="sm"
          items={[{ label: t('integrations.workspaces.title'), path: '.' }]}
          onNavigate={(path) => navigate(path)}
        />

        <div className="mb-4 mt-4 border-b border-edge pb-4">
          <h1 className="text-2xl font-bold tracking-[-0.015em] text-content">
            {t('integrations.workspaces.title')}
          </h1>
          <p className="mt-1 max-w-3xl text-[13px] text-content-secondary">
            {t('integrations.workspaces.subtitle')}
          </p>
        </div>

        {isLoading ? (
          <Skeleton className="h-40 w-full" />
        ) : error ? (
          <StatusMessage icon={AlertTriangle} title={t('common.requestFailed')} subtitle={error} />
        ) : (
          <div className="flex flex-col gap-3">
            <Filters
              searchProps={searchProps}
              showAllFilters={false}
              sort={sort}
              onClearAll={handleClearAll}
            />

            {visible.length === 0 ? (
              <StatusMessage icon={Blocks} title={t('integrations.workspaces.noResults')} />
            ) : (
              <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 xl:grid-cols-4">
                {visible.map((row) => (
                  <button
                    key={row.id}
                    type="button"
                    onClick={() => setManaging(row.workspace)}
                    aria-label={t('integrations.workspaces.open', { name: row.workspace })}
                    className="group flex flex-col gap-2 rounded-[11px] border border-edge bg-white/[0.02] px-4 py-3.5 text-left transition-all hover:-translate-y-0.5 hover:border-accent/45 hover:bg-accent/[0.04]"
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: row.color }}
                        aria-hidden="true"
                      />
                      <span className="truncate text-sm font-bold text-content">{row.workspace}</span>
                    </div>
                    <p className="text-xs leading-snug text-content-secondary">{row.desc}</p>
                    <p className="flex items-center gap-1.5 text-[11.5px] text-content-faint">
                      <span>
                        <b className="font-bold text-content">{row.member_count}</b>{' '}
                        {t('integrations.workspaces.membersUnit')}
                      </span>
                      <span aria-hidden="true">·</span>
                      <span>
                        <b className="font-bold text-content">{row.integrations.length}</b>{' '}
                        {t('integrations.workspaces.integrationsUnit')}
                      </span>
                    </p>
                    <div className="mt-auto flex items-center justify-between border-t border-white/[0.05] pt-2">
                      <span className="text-[11px] text-content-faint">
                        {t('integrations.workspaces.ownedBy', { name: row.owner })}
                      </span>
                      <ArrowRight
                        size={14}
                        className="text-content-faint transition-transform group-hover:translate-x-0.5"
                        aria-hidden="true"
                      />
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {!isLoading && !error ? (
        <div className={`${PAGE_INNER} shrink-0 py-3 text-[12px] text-content-faint`}>
          {t('integrations.workspaces.rowsTotal', { count: filtered.length })}
        </div>
      ) : null}

      <WorkspaceManagePanel workspace={managing} onClose={() => setManaging(null)} onChanged={refetch} />
    </main>
  );
};

export default WorkspacesPage;
